import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Store, Settings, ChevronLeft } from 'lucide-react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import './withdrawal-overlay.scss';
import { Chip } from 'primereact/chip';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWithdrawalsAfterDate } from '../../../store/actions';
import { getNotifications, getShopsByUser } from '../../shopSlices';
import { getUserNotifsConfig, getUsers, updateUserNotificationsSettings } from "features/authentication/userSlices";

interface WithdrawalOverlayProps {
  onClose: () => void;
}

// Move SettingsView component outside the main component
const SettingsView = ({ 
  onBack, 
  email, 
  setEmail, 
  iceCreamThreshold, 
  setIceCreamThreshold, 
  packsThreshold, 
  setPacksThreshold, 
  notifications, 
  setNotifications, 
  onSave 
}) => {
  const emailInputRef = useRef(null);
  const iceCreamInputRef = useRef(null);
  const packsInputRef = useRef(null);
  
  return (
    <>
      <div className="overlay__header">
        <Button 
          icon={<ChevronLeft />}
          onClick={onBack}
          text
          rounded
        />
        <h2>Settings</h2>
      </div>

      <div className="overlay__settings">
        <div className="field">
          <label className="field-label">Email to receive notifications</label>
          <InputText
            ref={emailInputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="field-input"
          />
        </div>
        <div className="field">
          <label className="field-label">Receive notifications when</label>
          
          <div className="toggle">
            <div className="toggle-header">
              <div className="section-title">More than</div>
              {/* <InputSwitch
                checked={notifications.iceCream}
                onChange={(e) => setNotifications(prev => ({ ...prev, iceCream: e.value }))}
                style={{ 
                  '--primary-color': '#4F4BFF',
                  '--primary-hover-color': '#4F4BFF'
                } as React.CSSProperties}
              /> */}
            </div>
            <div className="toggle-content">
              <InputText
                ref={iceCreamInputRef}
                value={iceCreamThreshold.toString()}
                onChange={(e) => setIceCreamThreshold(parseInt(e.target.value) || 25)}
                className="field-input small"
              />
              <span>L of ice cream is out</span>
            </div>
          </div>

          <div className="separator thin" />

          <div className="toggle">
            <div className="toggle-header">
              <div className="section-title">More than</div>
              {/* <InputSwitch
                checked={notifications.packs}
                onChange={(e) => setNotifications(prev => ({ ...prev, packs: e.value }))}
              /> */}
            </div>
            <div className="toggle-content">
              <InputText
                ref={packsInputRef}
                value={packsThreshold.toString()}
                onChange={(e) => setPacksThreshold(parseInt(e.target.value) || 5)}
                className="field-input small"
              />
              <span>packs is out</span>
            </div>
          </div>
        </div>
        <Button
          label="Save"
          onClick={onSave}
          className="save-button"
        />
      </div>
    </>
  );
};

const WithdrawalOverlay: React.FC<WithdrawalOverlayProps> = ({ onClose }) => {
  const dispatch: Function = useDispatch();
  const [showSettings, setShowSettings] = useState(false);
  const [email, setEmail] = useState('');
  const userNotifsConfig = useSelector((state: any) => state.user.userNotifsConfig);
  const [iceCreamThreshold, setIceCreamThreshold] = useState(20);
  const [packsThreshold, setPacksThreshold] = useState(3);
  const [notifications, setNotifications] = useState({
    iceCream: true,
    packs: true
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const selectedShop = useSelector((state: any) => state.user.selectedShop);
  const stockNotifications = useSelector((state: any) => state.shop.notifications);
  const user = useSelector((state: any) => state.auth.user);
  // Get date from localStorage or create and store one week ago date
  let lastDeletionTimestamp = localStorage.getItem('lastDeletionTimestamp');
  
  // If no timestamp exists in localStorage, create one for a week ago and store it
  if (!lastDeletionTimestamp) {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    // Store the new timestamp in localStorage
    localStorage.setItem('lastDeletionTimestamp', oneWeekAgo.toISOString());
    lastDeletionTimestamp = oneWeekAgo.toISOString();
  }
  
  // Use the timestamp from localStorage (either existing or newly created)
  const date = new Date(lastDeletionTimestamp);
  
  const users = useSelector((state: any) => state.user.users)
  const shops = useSelector((state: any) => state.shop.shops)
  const userIdToName = useMemo(() => {
      return users.reduce((acc, user) => {
          acc[user.user_id] = user.name;
          return acc;
      }, {});
      
  }, [users]);

  const shopIdToName = useMemo(() => {
      console.log(shops)
      return shops.reduce((acc, shop) => {
          acc[shop.id] = shop.name;
          return acc;
      }, {});
  }, [shops]);

  // Fetch notifications function
  const fetchNotifications = () => {
    if(user.sub){
      if(!users.length){
        dispatch(getUsers(user.sub))
      }
      if(!shops.length){
        dispatch(getShopsByUser(user.sub))
      }
    }
    let savedIceCreamThreshold = userNotifsConfig?.ic_threshold || localStorage.getItem('iceCreamThreshold');
    if(!savedIceCreamThreshold){
      localStorage.setItem('iceCreamThreshold', '0');
      savedIceCreamThreshold = '0';
    }
    const formattedDate = date.toISOString();
    dispatch(getNotifications(selectedShop, savedIceCreamThreshold, formattedDate));
  };

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [lastDeletionTimestamp]);

  useEffect(() => {
    dispatch(getUserNotifsConfig(user.sub, selectedShop))
    localStorage.setItem('lastViewedNotifications', new Date().toISOString());
    setUnreadCount(0);
  }, []);

  const withdrawals = stockNotifications.map((notif) => {
    return {
      location: shopIdToName[notif.shop_id],
      action: `${userIdToName[notif.user_id]} a retiré ${notif.quantity*-1}L de ${notif.name}`
    }
  })
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const overlay = document.querySelector('.overlay');
      const bell = document.querySelector('.NotificationBell');
      if (overlay && 
          !overlay.contains(event.target as Node) && 
          !bell?.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const storedDeletionDate = localStorage.getItem('lastDeletionTimestamp');
    if (storedDeletionDate) {
      dispatch(fetchWithdrawalsAfterDate(storedDeletionDate));
    }
  }, [dispatch]);

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedEmail = userNotifsConfig?.email || localStorage.getItem('notificationEmail');
    const savedIceCreamThreshold = userNotifsConfig?.ic_threshold || localStorage.getItem('iceCreamThreshold');
    const savedPacksThreshold = userNotifsConfig?.op_threshold || localStorage.getItem('packsThreshold');
    const savedNotifications = localStorage.getItem('notificationToggles');
    
    if (savedEmail) {
      setEmail(savedEmail);
    }
    
    if (savedIceCreamThreshold) {
      setIceCreamThreshold(parseInt(savedIceCreamThreshold));
    }
    
    if (savedPacksThreshold) {
      setPacksThreshold(parseInt(savedPacksThreshold));
    }
    
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    }
  }, [userNotifsConfig]);


  // Memoize the handlers to prevent unnecessary re-renders
  const handleBackToMain = useCallback(() => {
    setShowSettings(false);
  }, []);
  
  const handleSaveSettings = useCallback(() => {
    // Save settings to localStorage
    localStorage.setItem('notificationEmail', email);
    localStorage.setItem('iceCreamThreshold', iceCreamThreshold.toString());
    localStorage.setItem('packsThreshold', packsThreshold.toString());
    localStorage.setItem('notificationToggles', JSON.stringify(notifications));
    console.log(iceCreamThreshold)
    dispatch(updateUserNotificationsSettings(user.sub, selectedShop, email, iceCreamThreshold, packsThreshold));
    
    setShowSettings(false);
  }, [email, iceCreamThreshold, packsThreshold, notifications, user.sub, selectedShop, dispatch]);

  const WithdrawalsList = () => {
    const handleDeleteAll = () => {
      const deletionTimestamp = new Date().toISOString();
      localStorage.setItem('lastDeletionTimestamp', deletionTimestamp);
      localStorage.setItem('lastViewedNotifications', deletionTimestamp);
      dispatch(fetchWithdrawalsAfterDate(deletionTimestamp));
    };

    return (
      <>
        <div className="overlay__header">
          <h2 onClick={handleDeleteAll}>Delete all</h2>
          <Button 
            icon={<Settings />}
            onClick={() => setShowSettings(true)}
            text
            rounded
          />
        </div>
        
        <div className="overlay__list">
          {withdrawals.map((item, index) => (
            <div key={index} className="overlay__list-item">
              <div className="location">
                <Chip label={item.location} icon="pi pi-shop" className="chip" />
              </div>
              <div className="action">{item.action}</div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="overlay">
      <div className="overlay__container">
        <div className={`slide-container ${showSettings ? 'show-settings' : ''}`}>
          <div className="main-view">
            <WithdrawalsList />
          </div>
          <div className="settings-view">
            <SettingsView 
              onBack={handleBackToMain}
              email={email}
              setEmail={setEmail}
              iceCreamThreshold={iceCreamThreshold}
              setIceCreamThreshold={setIceCreamThreshold}
              packsThreshold={packsThreshold}
              setPacksThreshold={setPacksThreshold}
              notifications={notifications}
              setNotifications={setNotifications}
              onSave={handleSaveSettings}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalOverlay;